import { Alert, List, ListItem, Stack, Typography } from '@mui/material';
import { BulkOperationError } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import ModalTemplate from 'v2/components/Modal/ModalTemplate';
import {
  BulkUserActionFailure,
  getBulkUserActionFailures,
  getFailedVisitorIds,
  SelectedUser
} from './bulkUserActionErrorsUtils';

const FAILURE_LIST_MAX_HEIGHT = 384;

interface Props {
  errors: BulkOperationError[];
  selectedUsers: SelectedUser[];
  title: string;
  loading: boolean;
  errorMessage?: string;
  onRetry: (failedVisitorIds: string[]) => void;
}

const BulkUserActionFailuresModal: React.FC<Props> = ({
  errors,
  selectedUsers,
  title,
  loading,
  errorMessage,
  onRetry
}) => {
  const { translation } = useTranslation();
  const failures = getBulkUserActionFailures(errors, selectedUsers, translation.bulk_action_unknown_failure_reason);

  return (
    <ModalTemplate
      title={title}
      primaryAction={{
        id: 'retry-bulk-user-action-button',
        label: translation.retry,
        onClick: () => onRetry(getFailedVisitorIds(errors)),
        loading,
        disabled: loading
      }}
      secondaryAction={{ id: 'cancel-retry-bulk-user-action-button', label: translation.cancel }}
    >
      <Stack gap={3}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        <Typography>{translation.bulk_action_failures_description}</Typography>
        <List
          disablePadding
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            maxHeight: FAILURE_LIST_MAX_HEIGHT,
            overflowY: 'auto'
          }}
        >
          {failures.map((failure, index) => (
            <FailureListItem key={`${failure.visitorId}-${index}`} failure={failure} />
          ))}
        </List>
      </Stack>
    </ModalTemplate>
  );
};

interface FailureListItemProps {
  failure: BulkUserActionFailure;
}

const FailureListItem: React.FC<FailureListItemProps> = ({ failure }) => (
  <ListItem
    sx={(theme) => ({
      alignItems: 'flex-start',
      border: 1,
      borderColor: theme.palette.divider,
      borderRadius: theme.shape.borderRadius,
      px: 2,
      py: 1.5
    })}
  >
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 0.5, sm: 2 }} width="100%">
      <Typography fontWeight="bold">{failure.name}</Typography>
      <Typography color="textSecondary">{failure.reason}</Typography>
    </Stack>
  </ListItem>
);

export default BulkUserActionFailuresModal;
